import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import SelectedPackageSubscriptions from "./SelectedPackageSubscriptions";
import {
  DatePicker,
  ErrorMessage,
  PrimaryButton,
  RadioButton,
  Textfield,
} from "widgets";
import {
  useFetchUpdatedPrice,
  useSearchSalesAgent,
} from "store/backend/assets";
import {
  DISCOUNT_MODES,
  DISCOUNT_TYPE,
  INR_SYMBOL,
  MIN_COUPON_CODE_LENGTH,
} from "constants/config";
import { useSearchParams } from "react-router-dom";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import dayjs from "dayjs";
import AddressForm from "components/AddressForm/AddressForm";
import Search from "widgets/Dropdown/SearchableSingleSelect";

import styles from "./OrderSummary.module.scss";

const calculateTotalMRP = (selectedPackageSubscriptions) => {
  const totalMrp = selectedPackageSubscriptions.reduce(
    (total, packageSubscription) => {
      const mrp = packageSubscription.mrp;
      return total + mrp;
    },
    0
  );
  return reduceToFixedDecimals(totalMrp);
};

const handleDynamicDiscount = (
  discount,
  setErrorMessage,
  setFieldValue,
  setDiscountValue
) => {
  setDiscountValue(discount);
  const trimmedDiscount = discount.trim();
  const isPercentage = trimmedDiscount.includes("%");
  const numericValue = parseFloat(trimmedDiscount.replace("%", ""));
  const isValidFormat = /^\d+(\.\d{1,2})?%?$/.test(trimmedDiscount);

  if (!discount) {
    setFieldValue("orderDetails.discount", null);
    return;
  }

  if (!isValidFormat) {
    setErrorMessage(
      "Enter a valid number or percentage (e.g., 1000, 10.5, or 10.25%)."
    );
    return;
  }

  if (isNaN(numericValue)) {
    setErrorMessage("Invalid numeric value.");
    return;
  }

  if (isPercentage && numericValue > 100) {
    setErrorMessage("Percentage cannot be more than 100%.");
    return;
  }

  setErrorMessage("");
  const dynamicDiscountPayload = {
    discount_value: numericValue,
    is_percentage: isPercentage,
    discount_type: DISCOUNT_TYPE.DYNAMIC,
  };
  setFieldValue("orderDetails.discount", dynamicDiscountPayload);
};

const handleCodeDiscount = (
  couponCode,
  setCouponCode,
  discountValue,
  setDiscountValue,
  setErrorMessage,
  setFieldValue,
  customer_id
) => {
  setCouponCode(couponCode);
  setDiscountValue(discountValue);
  const trimmedDiscount = discountValue?.trim();
  const isPercentage = trimmedDiscount?.includes("%");
  const numericValue = parseFloat(trimmedDiscount?.replace("%", ""));
  const isValidFormat = /^\d+(\.\d{1,2})?%?$/.test(trimmedDiscount);

  if (discountValue && !isValidFormat) {
    setErrorMessage(
      "Enter a valid number or percentage (e.g., 1000, 10.5, or 10.25%)."
    );
    return;
  }

  if (discountValue && isNaN(numericValue)) {
    setErrorMessage("Invalid numeric value.");
    return;
  }

  if (isPercentage && numericValue > 100) {
    setErrorMessage("Percentage cannot be more than 100%.");
    return;
  }

  setErrorMessage("");

  const couponDiscountPayload = {
    coupon_code: couponCode,
    customer_id: customer_id,
    discount_type: DISCOUNT_TYPE.COUPON_CODE,
    ...(discountValue && {
      discount_value: numericValue,
      is_percentage: isPercentage,
    }),
  };

  setFieldValue("orderDetails.discount", couponDiscountPayload);
};

const handleRemoveDiscount = (
  queryClient,
  setFieldValue,
  setIsValueApplied,
  setValue,
  mutationVariables
) => {
  setValue("");
  setIsValueApplied(false);
  setFieldValue("priceDetailsAfterDiscount", {});
  queryClient.removeQueries({ queryKey: ["discount", mutationVariables] });
};

const handleRemoveUpdatedDiscount = async (
  queryClient,
  setFieldValue,
  setIsValueApplied,
  setValue,
  refetchUpdatedPrice,
  setIsCodeApplied,
  setErrorMessage,
  handleApplyDiscount,
  discount,
  mutationVariables
) => {
  await queryClient.removeQueries({
    queryKey: ["discount", mutationVariables],
  });
  setValue("");
  setIsValueApplied(false);
  setFieldValue("priceDetailsAfterDiscount", {});

  await setFieldValue("orderDetails.discount", {
    coupon_code: discount.coupon_code,
    customer_id: discount.customer_id,
    discount_type: discount.discount_type,
  });

  await handleApplyDiscount(
    refetchUpdatedPrice,
    setFieldValue,
    setIsCodeApplied,
    setErrorMessage
  );
};

const changeDiscountMode = (
  value,
  setDiscountMode,
  setCouponCode,
  setDiscountValue,
  setIsCodeApplied,
  setErrorMessage,
  setIsDiscountValueApplied,
  setFieldValue,
  queryClient,
  mutationVariables
) => {
  setDiscountMode(value);
  setCouponCode("");
  setDiscountValue("");
  setIsCodeApplied(false);
  setErrorMessage("");
  setIsDiscountValueApplied(false);
  setFieldValue("orderDetails.discount", null);
  queryClient.removeQueries({ queryKey: ["discount", mutationVariables] });
};

const buildMutationVariables = (
  selectedPackageSubscriptions,
  discount,
  selectedVerticalId
) => {
  const skus = selectedPackageSubscriptions.map((packageSubscription) => ({
    id: packageSubscription.packageId,
    subscriptions: [
      {
        id: packageSubscription.id,
        price: packageSubscription.mrp,
        books_price: packageSubscription.booksPrice || 0,
      },
    ],
  }));

  const mutationVariables = {
    skus,
    platform_id: 1,
    vertical_id: selectedVerticalId,
    discount,
  };

  return mutationVariables;
};

const getPriceAfterDiscount = (updatedPriceDetails) => {
  const priceAfterDiscount = updatedPriceDetails?.price_after_discount;
  if (priceAfterDiscount != null) {
    return reduceToFixedDecimals(priceAfterDiscount);
  } else {
    return null;
  }
};

const handleApplyDiscount = (
  refetchUpdatedPrice,
  setFieldValue,
  setIsValueApplied,
  setErrorMessage
) => {
  refetchUpdatedPrice()
    .then((result) => {
      if (result.error) {
        setErrorMessage(
          result?.error?.message ||
            "An error occurred while processing the request."
        );
        return;
      }
      setFieldValue("priceDetailsAfterDiscount", {
        listedOrderAmount: result.data.total_order_amount,
        orderAmount: result.data.price_after_discount,
        subscription_discount_details:
          result.data.subscription_discount_details || [],
      });
      setIsValueApplied(true);
    })
    .catch((err) => {
      setErrorMessage("An error occurred while processing the request.");
    });
};

const handleUseSameAddress = (useSameAddress, setFieldValue, orderDetails) => {
  const newUseSameAddress = !useSameAddress;
  setFieldValue("useSameAddress", newUseSameAddress);

  if (newUseSameAddress) {
    setFieldValue("orderDetails", {
      ...orderDetails,
      shippingAddress: orderDetails.billingAddress,
    });
  } else {
    setFieldValue("orderDetails", {
      ...orderDetails,
      shippingAddress: {},
    });
  }
};

const getAgents = (agentsData, isAgentsLoading) => {
  if (isAgentsLoading || !agentsData) {
    return [];
  }
  return agentsData.map((agent) => ({
    label: agent.label,
    value: agent.key,
    raw: agent,
  }));
};

const OrderSummary = (props) => {
  const {
    orderDetails,
    selectedVerticalId,
    selectedPackageSubscriptions,
    setFieldValue,
    useSameAddress,
    isOrderTransfer,
    errors,
    handleBlur,
    validateForm,
  } = props;

  const { discount, serviceStartDate, attributedTo, attributedToName } =
    orderDetails;
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const customer_id = searchParams.get("uamId");
  const [discountMode, setDiscountMode] = useState(DISCOUNT_MODES[0].value);
  const [couponCode, setCouponCode] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [isCodeApplied, setIsCodeApplied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDiscountValueApplied, setIsDiscountValueApplied] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const totalMRP = calculateTotalMRP(selectedPackageSubscriptions);
  const discoutMutationVariables = buildMutationVariables(
    selectedPackageSubscriptions,
    discount,
    selectedVerticalId
  );
  const {
    isLoading: isUpdatedPriceLoading,
    data: updatedPriceDetails,
    refetch: refetchUpdatedPrice,
  } = useFetchUpdatedPrice(discoutMutationVariables);

  const { isLoading: isAgentsLoading, data: agentsData } = useSearchSalesAgent(
    searchTerm,
    true
  );

  return (
    <div className={styles.orderSummaryContainer}>
      <AddressForm
        billingAddress={orderDetails.billingAddress}
        shippingAddress={orderDetails.shippingAddress}
        handleChangeBillingAddress={(fieldName, fieldValue) =>
          setFieldValue(`orderDetails.billingAddress.${fieldName}`, fieldValue)
        }
        handleChangeShippingAddress={(fieldName, fieldValue) =>
          setFieldValue(`orderDetails.shippingAddress.${fieldName}`, fieldValue)
        }
        useSameAddress={useSameAddress}
        handleUseSameAddress={() =>
          handleUseSameAddress(useSameAddress, setFieldValue, orderDetails)
        }
        hasHardware={orderDetails.hasHardware}
        errors={errors}
        handleBlur={handleBlur}
        isOrderTransfer={isOrderTransfer}
        validateForm={validateForm}
      />
      <div className={styles.flexContainer}>
        <div className={styles.startServiceDate}>
          <div className={styles.startServiceDateTitle}>
            Select Start Service Date*
          </div>
          <DatePicker
            value={serviceStartDate}
            onChange={(date) =>
              setFieldValue("orderDetails.serviceStartDate", date)
            }
            minDate={dayjs()}
          />
        </div>
        <div className={styles.attributedTo}>
          <div className={styles.attributedToTitle}>Select L0 Sales Person*</div>
          <Search
            id="attributedTo"
            name="attributedTo"
            placeholder="Search agent"
            options={agentsData || []}
            value={
              (agentsData || []).find(
                (opt) => opt.value === orderDetails.attributedTo
              ) ||
              (orderDetails.attributedToName
                ? {
                    label: orderDetails.attributedToName,
                    value: orderDetails.attributedTo,
                  }
                : null)
            }
            getOptionLabel={(option) => option?.label ?? ""}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            loading={isAgentsLoading}
            handleOnChangeSearch={(text) => {
              setSearchTerm(text);
              if (text === "") {
                setFieldValue("orderDetails.attributedTo", "");
                setFieldValue("orderDetails.attributedToName", "");
              }
            }}
            handleOnSelectSearchItem={(selected) => {
              if (!selected) {
                setFieldValue("orderDetails.attributedTo", "");
                setFieldValue("orderDetails.attributedToName", "");
                return;
              }
              setFieldValue("orderDetails.attributedTo", selected.value);
              setFieldValue("orderDetails.attributedToName", selected.label);
              setSearchTerm("");
            }}
            onClose={() => {}}
          />
        </div>
        <div className={styles.packageContainer}>
          <SelectedPackageSubscriptions
            selectedPackageSubscriptions={selectedPackageSubscriptions}
            disableDeleteIcon={true}
          />
        </div>
        <div className={styles.discountContainer}>
          <div className={styles.discountField}>
            <span className={styles.discountLabel}>Total Cart Value:</span>
            <span className={styles.amountValues}>
              {INR_SYMBOL + totalMRP.toLocaleString()}
            </span>
          </div>
          <span className={styles.discountLabel}>
            Select the Mode of Discount:
          </span>
          <div>
            <RadioButton
              value={discountMode}
              options={DISCOUNT_MODES}
              onChange={(event) =>
                changeDiscountMode(
                  event.target.value,
                  setDiscountMode,
                  setCouponCode,
                  setDiscountValue,
                  setIsCodeApplied,
                  setErrorMessage,
                  setIsDiscountValueApplied,
                  setFieldValue,
                  queryClient,
                  discoutMutationVariables
                )
              }
              row={true}
            />
          </div>
          {discountMode === "dynamic" && (
            <div className={styles.discountTextFieldContainer}>
              <div className={styles.discountTextField}>
                <Textfield
                  label="Enter amount or %"
                  value={discountValue}
                  handleOnChange={(event) => {
                    const dynamicValue = event.target.value;
                    handleDynamicDiscount(
                      dynamicValue,
                      setErrorMessage,
                      setFieldValue,
                      setDiscountValue
                    );
                  }}
                  disabled={isDiscountValueApplied}
                />
                {!isDiscountValueApplied ? (
                  !isUpdatedPriceLoading ? (
                    <PrimaryButton
                      onClick={() =>
                        handleApplyDiscount(
                          refetchUpdatedPrice,
                          setFieldValue,
                          setIsDiscountValueApplied,
                          setErrorMessage
                        )
                      }
                      disabled={!discountValue || errorMessage}
                    >
                      Apply
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton disabled={true}>Applying...</PrimaryButton>
                  )
                ) : (
                  <PrimaryButton
                    onClick={() =>
                      handleRemoveDiscount(
                        queryClient,
                        setFieldValue,
                        setIsDiscountValueApplied,
                        setDiscountValue,
                        discoutMutationVariables
                      )
                    }
                  >
                    Remove
                  </PrimaryButton>
                )}
              </div>
            </div>
          )}
          {discountMode === "code" && (
            <div className={styles.discountTextFieldContainer}>
              <div className={styles.discountTextField}>
                <Textfield
                  label="Enter code"
                  value={couponCode}
                  handleOnChange={(event) => {
                    const code = event.target.value;
                    handleCodeDiscount(
                      code,
                      setCouponCode,
                      discountValue,
                      setDiscountValue,
                      setErrorMessage,
                      setFieldValue,
                      customer_id
                    );
                  }}
                  disabled={isCodeApplied}
                />
                {!isCodeApplied ? (
                  !isUpdatedPriceLoading ? (
                    <PrimaryButton
                      onClick={() =>
                        handleApplyDiscount(
                          refetchUpdatedPrice,
                          setFieldValue,
                          setIsCodeApplied,
                          setErrorMessage
                        )
                      }
                      disabled={
                        !couponCode ||
                        couponCode.length < MIN_COUPON_CODE_LENGTH ||
                        errorMessage
                      }
                    >
                      Apply
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton disabled={true}>Applying...</PrimaryButton>
                  )
                ) : (
                  <PrimaryButton
                    onClick={() => {
                      handleRemoveDiscount(
                        queryClient,
                        setFieldValue,
                        setIsCodeApplied,
                        setCouponCode,
                        discoutMutationVariables
                      );
                      handleRemoveDiscount(
                        queryClient,
                        setFieldValue,
                        setIsDiscountValueApplied,
                        setDiscountValue,
                        discoutMutationVariables
                      );
                    }}
                  >
                    Remove
                  </PrimaryButton>
                )}
              </div>
              {isCodeApplied && (
                <div className={styles.discountTextField}>
                  <Textfield
                    label="Enter amount or %"
                    value={discountValue}
                    handleOnChange={(event) => {
                      const discount = event.target.value;
                      handleCodeDiscount(
                        couponCode,
                        setCouponCode,
                        discount,
                        setDiscountValue,
                        setErrorMessage,
                        setFieldValue,
                        customer_id
                      );
                    }}
                    disabled={isDiscountValueApplied}
                  />
                  {!isDiscountValueApplied ? (
                    !isUpdatedPriceLoading ? (
                      <PrimaryButton
                        onClick={() =>
                          handleApplyDiscount(
                            refetchUpdatedPrice,
                            setFieldValue,
                            setIsDiscountValueApplied,
                            setErrorMessage
                          )
                        }
                        disabled={!discountValue || errorMessage}
                      >
                        Apply
                      </PrimaryButton>
                    ) : (
                      <PrimaryButton disabled={true}>Applying...</PrimaryButton>
                    )
                  ) : !isUpdatedPriceLoading ? (
                    <PrimaryButton
                      onClick={() => {
                        handleRemoveUpdatedDiscount(
                          queryClient,
                          setFieldValue,
                          setIsDiscountValueApplied,
                          setDiscountValue,
                          refetchUpdatedPrice,
                          setIsCodeApplied,
                          setErrorMessage,
                          handleApplyDiscount,
                          discount,
                          discoutMutationVariables
                        );
                      }}
                    >
                      Remove
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton disabled={true}>Removing...</PrimaryButton>
                  )}
                </div>
              )}
            </div>
          )}
          {!errorMessage && updatedPriceDetails && (
            <span className={styles.successMessage}>
              {" "}
              Coupon Applied Successfully. Max Discount applicable is Rs{" "}
              {(
                Math.floor(
                  (updatedPriceDetails.total_order_amount -
                    updatedPriceDetails.price_after_discount) *
                    100
                ) / 100
              ).toLocaleString()}
            </span>
          )}
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <div>
            <div className={styles.discountField}>
              <span className={styles.discountLabel}>Grand Total:</span>
              {isUpdatedPriceLoading ? (
                <span className={styles.amountValues}>
                  Grand Total Loading...
                </span>
              ) : (
                <span className={styles.amountValues}>
                  {INR_SYMBOL +
                    (
                      getPriceAfterDiscount(updatedPriceDetails) ?? totalMRP
                    ).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
